import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  ArrowUpRight,
  Globe2,
  Handshake,
  TrendingUp,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const spvImage = "/assets/images/spv.jpg";

const features = [
  {
    title: "Investment Promotion",
    text:
      "Engaging potential investors and promoting investment opportunities across Nigeria’s Oil & Gas Free Zones.",
    icon: Globe2,
  },
  {
    title: "Public-Private Partnerships",
    text:
      "Facilitating strategic public-private partnerships on behalf of OGFZA.",
    icon: Handshake,
  },
  {
    title: "Sustainable Development",
    text:
      "Supporting accelerated growth and sustainable development through investment-driven initiatives.",
    icon: TrendingUp,
  },
];

const AboutSPV = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power3.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 60,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".soft-card").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 50,
          scale: 0.95,
          rotateX: 8,
          delay: index * 0.08,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 92%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white bg-[#041018]">
        <img
          src={heroBg}
          alt="About SPV"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        <div className="absolute inset-0 bg-[#041018]/70" />

        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/90 via-[#082567]/60 to-[#026B07]/35" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/35 blur-3xl" />

        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/15 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Special Purpose Vehicle
          </div>

          <h1
            className="hero-title text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            About SPV
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Freezone Global Investments Ltd is the Special Purpose Vehicle of
            the Oil and Gas Free Zones Authority Nigeria.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75 flex-wrap">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/spv"
              className="hover:text-[#F2AF11] transition-colors"
            >
              SPV
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">About SPV</span>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal-up mb-14">
            <div className="rounded-[42px] overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC] shadow-[0_35px_100px_-45px_rgba(15,23,42,0.22)]">
              <img
                src={spvImage}
                alt="Freezone Global Investments Ltd"
                className="w-full h-auto object-contain object-center"
              />
            </div>
          </div>

          <div className="reveal-up max-w-5xl">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              A subsidiary of the Oil and Gas Free Zones Authority Nigeria
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#082567] leading-tight mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Freezone Global Investments Ltd
            </h2>

            <div className="space-y-7 text-slate-700 leading-[2] text-[16.5px]">
              <p>
                OGFZA’s Special Purpose Vehicle, (The SPV), Freezone Global
                Investments Ltd, was incorporated in 2015 as the investment
                subsidiary of the Oil and Gas Free Zones Authority Nigeria,
                OGFZA.
              </p>

              <p>
                The SPV engages with potential investors, promotes investment
                opportunities in our free zones and facilitates
                public-private-partnerships on behalf of the OGFZA in Nigeria’s
                oil & gas free zones, maximizing OGFZA’s potential as a vehicle
                for promoting accelerated growth and sustainable development.
              </p>
            </div>

            <a
              href="http://fzgil.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-10 px-8 py-4 rounded-full bg-[#082567] hover:bg-[#026B07] text-white font-black transition-all duration-500"
            >
              Contact The SPV
              <ArrowUpRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-7">
            {features.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="soft-card group relative overflow-hidden bg-white border border-[#E2E8F0] rounded-[34px] p-8 transition-all duration-700 hover:-translate-y-4 hover:bg-[#082567] hover:border-[#082567] hover:shadow-[0_40px_100px_-40px_rgba(8,37,103,0.55)]"
                >
                  <div className="absolute -top-20 -right-20 w-44 h-44 rounded-full bg-[#F2AF11]/0 group-hover:bg-[#F2AF11]/15 blur-2xl transition-all duration-700" />

                  <div className="absolute -bottom-24 -left-24 w-52 h-52 rounded-full bg-[#026B07]/0 group-hover:bg-[#026B07]/25 blur-2xl transition-all duration-700" />

                  <div className="relative z-10">
                    <div className="w-16 h-16 rounded-2xl bg-[#082567]/5 text-[#082567] flex items-center justify-center mb-8 transition-all duration-700 group-hover:bg-white/10 group-hover:text-[#F2AF11] group-hover:rotate-6 group-hover:scale-110">
                      <Icon size={30} />
                    </div>

                    <h3
                      className="text-3xl font-black text-[#082567] leading-tight mb-5 transition-colors duration-700 group-hover:text-white"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.9] transition-colors duration-700 group-hover:text-white/80">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA WITH IMAGE BACKGROUND */}
      <section className="relative py-28 overflow-hidden text-white">
        <img
          src={spvImage}
          alt="Investment Opportunities"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        <div className="absolute inset-0 bg-[#041018]/80" />

        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/90 via-[#082567]/70 to-[#026B07]/50" />

        <div className="absolute top-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/15 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Investment Opportunities
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-white leading-tight max-w-3xl"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Explore investment opportunities across Nigeria’s Oil & Gas Free
                Zones.
              </h2>
            </div>

            <a
              href="http://fzgil.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#F2AF11] hover:bg-white text-[#082567] font-black transition-all duration-500 whitespace-nowrap hover:-translate-y-1"
            >
              Visit SPV Website
              <ArrowUpRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutSPV;